import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { identify, resetIdentity, track } from '../lib/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/**
 * v4.1 bugfix — Auth error localisation.
 * Backend HTTPException.detail values are English literals ("Invalid credentials",
 * "Email already registered", …). We map the known ones to i18n keys so the
 * user sees the message in the currently-selected language.
 *
 * Unknown backend messages fall through as-is (better than swallowing them
 * behind a generic "Login failed" and losing signal).
 */
const localiseAuthError = (t, backendDetail, fallbackKey) => {
  const map = {
    'Invalid credentials':      'auth.errorInvalidCredentials',
    'Invalid token':            'auth.errorInvalidToken',
    'Invalid token type':       'auth.errorInvalidToken',
    'Invalid refresh token':    'auth.errorInvalidToken',
    'Email already registered': 'auth.errorEmailAlreadyRegistered',
  };
  if (backendDetail && map[backendDetail]) {
    return t(map[backendDetail]);
  }
  if (backendDetail) return backendDetail;
  return t(fallbackKey);
};

export const AuthProvider = ({ children }) => {
  const { t } = useTranslation();
  const [user, setUser] = useState(null); // null = checking, false = not auth, object = authenticated
  const [loading, setLoading] = useState(true);
  const { i18n } = useTranslation();
  const lastSyncedLang = useRef(null);

  const applyServerLanguage = useCallback((lang) => {
    if (!lang || !['pl', 'en'].includes(lang)) return;
    // v4.1 bugfix — do NOT let the server value override an explicit client-side
    // choice. If the user has ever clicked the FlagToggle (or i18n has persisted
    // an explicit language for any reason), localStorage `hussar-lang` is set
    // and becomes authoritative. The server value only seeds the language on a
    // brand-new visit where localStorage is empty.
    // Without this guard, a stale server preferred_language would silently
    // switch the user back to the wrong language on every /auth/me response
    // (i.e. every page load), producing "hero flashes to English after logo
    // click" reports even when the user has explicitly selected Polish.
    let hasExplicitClientChoice = false;
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        hasExplicitClientChoice = !!window.localStorage.getItem('hussar-lang');
      }
    } catch {
      // localStorage unavailable — fall through and honour the server value.
    }
    const current = i18n.resolvedLanguage || i18n.language;
    if (hasExplicitClientChoice) {
      // Do not overwrite. Remember what the client is on so the sync-back
      // effect can push it to the server on the next explicit toggle.
      lastSyncedLang.current = current?.startsWith('en') ? 'en' : 'pl';
      return;
    }
    if (current !== lang) {
      i18n.changeLanguage(lang);
    }
    lastSyncedLang.current = lang;
  }, [i18n]);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      applyServerLanguage(response.data.preferred_language);
      identify(response.data.id, {
        email: response.data.email,
        subscription_status: response.data.subscription_status,
        role: response.data.role,
        preferred_language: response.data.preferred_language,
        cohort: response.data.cohort,
      });
    } catch (error) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, [applyServerLanguage]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Sync language changes back to the server when the user is logged in
  useEffect(() => {
    if (!user) return;
    const handleLangChange = async (lng) => {
      const normalised = lng?.startsWith('en') ? 'en' : 'pl';
      if (normalised === lastSyncedLang.current) return;
      lastSyncedLang.current = normalised;
      try {
        await axios.patch(
          `${API}/auth/me/preferences`,
          { preferred_language: normalised },
          { withCredentials: true }
        );
        setUser((prev) => (prev ? { ...prev, preferred_language: normalised } : prev));
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to persist language preference:', error);
        }
      }
    };
    i18n.on('languageChanged', handleLangChange);
    return () => {
      i18n.off('languageChanged', handleLangChange);
    };
  }, [user, i18n]);

  const register = async (email, password, name) => {
    try {
      const response = await axios.post(
        `${API}/auth/register`,
        { email, password, name },
        { withCredentials: true }
      );
      setUser(response.data);
      // Respect the server's stored preference for this account. For brand-new
      // accounts the backend defaults to 'pl' (set in auth_routes.register).
      // We intentionally do NOT push the current UI language to the server here:
      // doing so would overwrite the 'pl' default with whatever the user happens
      // to be browsing in. The user can switch language explicitly via the
      // FlagToggle or /settings, which triggers the languageChanged listener
      // below and syncs the choice to the server.
      applyServerLanguage(response.data.preferred_language || 'pl');
      lastSyncedLang.current = response.data.preferred_language || 'pl';
      identify(response.data.id, {
        email: response.data.email,
        subscription_status: response.data.subscription_status,
        role: response.data.role,
        cohort: response.data.cohort,
      });
      track('user_registered', { cohort: response.data.cohort });
      track('cohort_assigned', { cohort: response.data.cohort });
      return { success: true, cohort: response.data.cohort, nextRoute: response.data.next_route, role: response.data.role };
    } catch (error) {
      return {
        success: false,
        error: localiseAuthError(t, error.response?.data?.detail, 'auth.registrationFailed')
      };
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(response.data);
      applyServerLanguage(response.data.preferred_language);
      identify(response.data.id, {
        email: response.data.email,
        subscription_status: response.data.subscription_status,
        role: response.data.role,
        cohort: response.data.cohort,
      });
      track('user_logged_in', { method: 'password', cohort: response.data.cohort });
      return { success: true, nextRoute: response.data.next_route, role: response.data.role };
    } catch (error) {
      return {
        success: false,
        error: localiseAuthError(t, error.response?.data?.detail, 'auth.loginFailed')
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      track('user_logged_out');
      resetIdentity();
      setUser(false);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Logout error:', error);
      }
      resetIdentity();
      setUser(false);
    }
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      setUser(false);
    }
  };

  const value = {
    user,
    loading,
    register,
    login,
    logout,
    refreshUser,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
